import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import Defaultchat from "./defaultchat.jsx";
import "./whatsappchat.css";
import "./whatsapp_index.css";
import { getToken } from "../../function/token.jsx";
import domain from "../../../reusecomponent/domain.jsx";
import { ErrorToastContainer } from "../../function/CustomFunction.jsx";
import websocketService from "../../function/websocketService.jsx";
import Preloader from "../preloader.jsx";
import Customer from "./customer.jsx";
import Customerchat from "./customerchat.jsx";
import WebSocketStatus from "./chats_coponent/WebSocketStatus.jsx";
import WebSocketNotification from "./chats_coponent/WebSocketNotification.jsx";
import Startnewchat from "./chats_coponent/Startnewchat.jsx";
import Call from "./chats_coponent/Call.jsx";
import { endCallSession, createSDPoffer, acceptWhatsAppAnswer, setOnCallConnected, isLocalCallActive } from "../../function/Calling.jsx";

export const ChatContext = createContext();

// Tab definitions — module-level constant so it's not recreated on every render
const TABS = [
  { id: "active", label: "Active" },
  { id: "ai", label: "AI" },
  { id: "intervent", label: "Intervent" },
];

// ─── Inline Loading Indicator ─────────────────────────────────────────────────
const LoadingIndicator = (() => {
  const listeners = new Set();
  return {
    Component: React.memo(function LoadingDot() {
      const [visible, setVisible] = useState(false);
      useEffect(() => {
        listeners.add(setVisible);
        return () => listeners.delete(setVisible);
      }, []);
      if (!visible) return null;
      return (
        <div className="text-center py-2">
          <div className="spinner-border spinner-border-sm text-secondary" role="status">
            <span className="visually-hidden">Loading more…</span>
          </div>
        </div>
      );
    }),
    show() { listeners.forEach(fn => fn(true)); },
    hide() { listeners.forEach(fn => fn(false)); },
  };
})();

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns true if the timestamp is within the last 24 hours. */
function isWithin24Hours(timestamp) {
  if (!timestamp) return false;
  return (new Date() - new Date(timestamp)) / 3_600_000 < 24;
}

/** Determines if a contact is within the active 24-hour reply window */
function isCustomerActive(c) {
  if (c?.can_reply === true || c?.can_reply === 1 || c?.can_reply === "true" || c?.can_reply === "1") return true;
  if (c?.can_reply === false || c?.can_reply === 0 || c?.can_reply === "false" || c?.can_reply === "0") return false;
  return isWithin24Hours(c?.receiver_last_reply_at || c?.last_update_time);
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Whatsappchat() {
  const customersRef = useRef(null);
  const activeTabRef = useRef("active");
  const [activeTab, setActiveTab] = useState("active");

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollbottomRef = useRef(true);
  const searchRef = useRef(null);

  const [wadetails, setWadetails] = useState(null);
  const wadetailsRef = useRef(null);
  const [customerschat, setCustomerschat] = useState(null);

  const caller_detils = useRef(null);
  const caller_exits = useRef(null);
  const [callVisible, setCallVisible] = useState(false);
  const [calling_status, setCallingStatus] = useState("Calling...");
  const callVisibleRef = useRef(false);
  const callingStatusRef = useRef("Calling...");

  const updateCallVisible = useCallback((val) => {
    callVisibleRef.current = val;
    setCallVisible(val);
  }, []);

  const updateCallingStatus = useCallback((val) => {
    if (typeof val === 'function') {
      setCallingStatus((prev) => {
        const next = val(prev);
        callingStatusRef.current = next;
        return next;
      });
    } else {
      callingStatusRef.current = val;
      setCallingStatus(val);
    }
  }, []);

  useEffect(() => {
    setOnCallConnected(() => {
      updateCallingStatus("Connected");
    });
  }, [updateCallingStatus]);

  const incomingCallService = useSelector((state) => state?.serviceStore?.value?.incoming_call);
  const incomingCallServiceRef = useRef(incomingCallService);
  useEffect(() => {
    incomingCallServiceRef.current = incomingCallService;
  }, [incomingCallService]);

  const callRecordingService = useSelector((state) => 
    state?.serviceStore?.value?.call_recording ?? 
    state?.serviceStore?.value?.call_recoding ?? 
    state?.serviceStore?.value?.recording
  );
  const isCallRecordingEnabled = Boolean(
    callRecordingService && 
    callRecordingService !== "0" && 
    callRecordingService !== 0
  );

  // ── Search ─────────────────────────────────────────────────────────────────

  /** Applies the search box filter to a given list (or the current tab's list). */
  const applySearch = useCallback((customerList) => {
    const list = Array.isArray(customerList) ? customerList : [];
    if (!searchRef.current) return;
    const searchValue = searchRef.current.value?.trim().toLowerCase() ?? "";
    if (!searchValue) {
      setCustomers(list);
      return;
    }
    setCustomers(list.filter(item => {
      const wa_id = item?.wa_id?.toLowerCase() ?? "";
      const name = item?.name?.toLowerCase() ?? "";
      const contact_name = item?.contact_name?.toLowerCase() ?? "";
      return wa_id.includes(searchValue) || name.includes(searchValue) || contact_name.includes(searchValue);
    }));
  }, []);

  // ── Tab filters ────────────────────────────────────────────────────────────

  const active = useCallback(() => {
    const list = Array.isArray(customersRef.current?.data) ? customersRef.current.data : [];
    const filtered = list.filter(c => isCustomerActive(c));
    setCustomers(filtered);
    applySearch(filtered);
  }, [applySearch]);

  const ai = useCallback(() => {
    const list = Array.isArray(customersRef.current?.data) ? customersRef.current.data : [];
    const filtered = list.filter(c => c?.last_chat_ai_agent);
    setCustomers(filtered);
    applySearch(filtered);
  }, [applySearch]);

  const intervent = useCallback(() => {
    const list = Array.isArray(customersRef.current?.data) ? customersRef.current.data : [];
    const filtered = list.filter(c => !isCustomerActive(c));
    setCustomers(filtered);
    applySearch(filtered);
  }, [applySearch]);

  /** Re-applies the current tab filter — uses the ref so it's stable across closures */
  const reapplyTabFilter = useCallback(() => {
    switch (activeTabRef.current) {
      case "active": active(); break;
      case "ai":     ai(); break;
      case "intervent": intervent(); break;
      default: break;
    }
  }, [active, ai, intervent]);

  const handleSearchChange = useCallback(() => {
    const list = Array.isArray(customersRef.current?.data) ? customersRef.current.data : [];
    let base;
    switch (activeTabRef.current) {
      case "active":    base = list.filter(c => isCustomerActive(c)); break;
      case "ai":        base = list.filter(c => c?.last_chat_ai_agent); break;
      case "intervent": base = list.filter(c => !isCustomerActive(c)); break;
      default:          base = list;
    }
    applySearch(base);
  }, [applySearch]);

  // ── Contact fetch ───────────────────────────────────────────────────────────

  const fetchContacts = useCallback(async (page = null) => {
    try {
      const token = getToken();
      const response = await fetch(
        page == null ? `${domain()}/chat-contact?page=1` : page,
        { method: "GET", headers: new Headers({ Authorization: `Bearer ${token}` }), redirect: "follow" }
      );
      const result = await response.json();
      if (result.status && result.data) {
        let decodedData = {};
        if (typeof result.data === 'string') {
          try {
            decodedData = JSON.parse(atob(result.data));
          } catch (e) {
            decodedData = {};
          }
        } else {
          decodedData = result.data;
        }

        scrollbottomRef.current = decodedData.next_page_url != null;
        const incomingData = Array.isArray(decodedData.data)
          ? decodedData.data
          : Array.isArray(decodedData)
          ? decodedData
          : [];

        if (customersRef.current === null) {
          customersRef.current = {
            data: incomingData,
            next_page_url: decodedData.next_page_url ?? null,
          };
          setCustomers(incomingData);
        } else {
          const currentList = Array.isArray(customersRef.current?.data) ? customersRef.current.data : [];
          customersRef.current = {
            data: [...currentList, ...incomingData],
            next_page_url: decodedData.next_page_url ?? null,
          };
        }
      } else {
        if (!customersRef.current) {
          customersRef.current = { data: [], next_page_url: null };
        }
        setCustomers(customersRef.current.data || []);
      }
    } catch (_) {
      if (!customersRef.current) {
        customersRef.current = { data: [], next_page_url: null };
      }
      setCustomers([]);
      ErrorToastContainer("Internal Server Error");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Infinite scroll ─────────────────────────────────────────────────────────

  const handleScroll = useCallback(async (e) => {
    const el = e.target;
    if (
      el.scrollTop + el.clientHeight >= el.scrollHeight - 10 &&
      scrollbottomRef.current &&
      customersRef.current?.next_page_url
    ) {
      // Prevent double-firing
      scrollbottomRef.current = false;
      LoadingIndicator.show();
      await fetchContacts(customersRef.current.next_page_url);
      // Re-apply tab filter after new data arrives
      reapplyTabFilter();
      LoadingIndicator.hide();
    }
  }, [fetchContacts, reapplyTabFilter]);

  // ── WebSocket message update helpers ────────────────────────────────────────

  const messageDeliverStatusUpdate = useCallback((status, messageId) => {
    setCustomerschat(prev => {
      const oldData = Array.isArray(prev?.data) ? prev.data : [];
      const index = oldData.findIndex(chat => chat.message_id === messageId);
      if (index === -1) return prev;
      const newData = [...oldData];
      newData[index] = { ...newData[index], message_deliver: status };
      return { data: newData, next_page_url: prev?.next_page_url };
    });
  }, []);

  const messageListUpdate = useCallback((data) => {
    if (!wadetailsRef.current || data.wa_id !== wadetailsRef.current.wa_id) return;
    if (data.type === "outgoing") {
      setCustomerschat(prev => {
        const oldData = Array.isArray(prev?.data) ? prev.data : [];
        const index = oldData.findIndex(
          chat => Number(chat.client_system_id) === Number(data.client_system_id)
        );
        const newData = index !== -1
          ? oldData.map((item, i) => i === index ? data : item)
          : [data, ...oldData];
        return { data: newData, next_page_url: prev?.next_page_url };
      });
    } else if (data.type === "incoming") {
      setCustomerschat(prev => ({
        data: [data, ...(Array.isArray(prev?.data) ? prev.data : [])],
        next_page_url: prev?.next_page_url,
      }));
    }
  }, []);

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  // Initial fetch
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // After initial load, apply active tab filter
  useEffect(() => {
    if (!loading && Array.isArray(customersRef.current?.data) && customersRef.current.data.length > 0) {
      active();
    }
  }, [loading, active]);

  // WebSocket setup
  useEffect(() => {
    websocketService.initialize();

    const unsubscribe = websocketService.onMessage((eventType, data) => {
      console.log('%c📡 [whatsappchat.jsx WS EVENT DISPATCH]:', 'color: #ff9100; font-weight: bold;', eventType, data);
      if (eventType !== "message") return;

      if (data.type === "message") {
        const newMessage = data.data;
        if (!newMessage) return;

        if (!customersRef.current) {
          customersRef.current = { data: [], next_page_url: null };
        }
        if (!Array.isArray(customersRef.current.data)) {
          customersRef.current.data = [];
        }

        // Update the sidebar contact list position
        const index = customersRef.current.data.findIndex(c => c?.wa_id === newMessage?.wa_id);
        const isIncoming = newMessage?.type !== "outgoing";
        const nowIso = new Date().toISOString();

        if (index > 0) {
          const [removed] = customersRef.current.data.splice(index, 1);
          removed.last_update_time = nowIso;
          if (isIncoming) {
            removed.receiver_last_reply_at = nowIso;
            removed.can_reply = true;
          } else {
            removed.sender_last_reply_at = nowIso;
          }
          customersRef.current.data.unshift(removed);
        } else if (index === 0) {
          customersRef.current.data[0].last_update_time = nowIso;
          if (isIncoming) {
            customersRef.current.data[0].receiver_last_reply_at = nowIso;
            customersRef.current.data[0].can_reply = true;
          } else {
            customersRef.current.data[0].sender_last_reply_at = nowIso;
          }
        } else if (index === -1) {
          customersRef.current.data.unshift({
            wa_id: newMessage.wa_id,
            name: newMessage.sender_name || newMessage.wa_id,
            contact_name: null,
            last_update_time: nowIso,
            receiver_last_reply_at: isIncoming ? nowIso : null,
            sender_last_reply_at: !isIncoming ? nowIso : null,
            can_reply: isIncoming,
          });
        }

        messageListUpdate(newMessage);
        reapplyTabFilter();

      } else if (data.type === "sent")      { messageDeliverStatusUpdate(1, data.data?.message_id); }
      else if (data.type === "delivered")   { messageDeliverStatusUpdate(2, data.data?.message_id); }
      else if (data.type === "read")        { messageDeliverStatusUpdate(3, data.data?.message_id); }
      else if (data.type === "failed")      { messageDeliverStatusUpdate(4, data.data?.message_id); }
      else if (data.type === "deleted")     { messageDeliverStatusUpdate(5, data.data?.message_id); }
      else if (data.type === "call" || data.calls || data.sdp_answer) {
        const rawCall = data?.data?.entry?.[0]?.changes?.[0]?.value?.calls?.[0] || data?.data?.entry?.[0]?.changes?.[0]?.value?.statuses?.[0] || data.data || data;
        const eventType = String(rawCall.event || rawCall.status || data.event || data.status || "").toLowerCase();
        const incomingCallId = String(rawCall.id || data.call_id || data.caller_id || "");
        
        let localActiveCallId = null;
        try { localActiveCallId = sessionStorage.getItem('active_accepted_call_id'); } catch (_) {}
        const isLocallyAcceptedCall = Boolean(
          (localActiveCallId && incomingCallId && (localActiveCallId === incomingCallId || incomingCallId.includes(localActiveCallId) || localActiveCallId.includes(incomingCallId))) ||
          isLocalCallActive()
        );

        let handledStore = null;
        try { handledStore = incomingCallId ? sessionStorage.getItem(`handled_call_${incomingCallId}`) : null; } catch (_) {}
        const isDismissedOrHandledElsewhere = Boolean(
          (handledStore === "mobile") ||
          (data.answered_by === "mobile") ||
          (data.answered_elsewhere && data.answered_by !== "web")
        );

        // 1. If mobile answered or handled elsewhere:
        if (data.answered_by === "mobile" || (data.answered_elsewhere && data.answered_by !== "web")) {
          if (!isLocallyAcceptedCall && !isLocalCallActive()) {
            try { 
              if (incomingCallId) sessionStorage.setItem(`handled_call_${incomingCallId}`, "mobile");
              sessionStorage.removeItem('active_accepted_call_id'); 
            } catch (_) {}
            endCallSession();
            caller_detils.current = null;
            caller_exits.current = null;
            updateCallingStatus("Calling...");
            updateCallVisible(false);
            return;
          }
        }

        // 2. Rejection / Termination events:
        if (["terminate", "reject", "missed", "failed", "declined"].includes(eventType)) {
          // If mobile sent an accidental reject while Web CRM is active locally, ignore mobile reject
          if (data.answered_by === "mobile" && (isLocallyAcceptedCall || isLocalCallActive())) {
            console.log("🛡️ [Web CRM] Ignoring mobile reject event because call is active locally on Web:", incomingCallId);
            return;
          }

          try { 
            sessionStorage.removeItem('active_accepted_call_id'); 
            if (incomingCallId) sessionStorage.removeItem(`handled_call_${incomingCallId}`);
          } catch (_) {}
          endCallSession();
          caller_detils.current = null;
          caller_exits.current = null;
          updateCallingStatus("Calling...");
          updateCallVisible(false);
          return;
        }

        const isIncomingCall = rawCall.direction !== "BUSINESS_INITIATED" && data.direction !== "BUSINESS_INITIATED";

        // 3. Incoming Call Accept events:
        if (isIncomingCall && ["accept", "accepted"].includes(eventType)) {
          if (data.answered_by === "web" || isLocallyAcceptedCall || isLocalCallActive()) {
            updateCallingStatus("Connected");
            updateCallVisible(true);
            return;
          } else {
            // Call answered on mobile -> close Web ringing modal
            try { 
              if (incomingCallId) sessionStorage.setItem(`handled_call_${incomingCallId}`, "mobile");
              sessionStorage.removeItem('active_accepted_call_id'); 
            } catch (_) {}
            endCallSession();
            caller_detils.current = null;
            caller_exits.current = null;
            updateCallingStatus("Calling...");
            updateCallVisible(false);
            return;
          }
        }

        // 4. Suppress duplicate ringing popup if call is already handled on mobile or active locally
        if (isIncomingCall && ["connect", "ringing", "offer", "pre_accept"].includes(eventType)) {
          if (isDismissedOrHandledElsewhere || isLocallyAcceptedCall || isLocalCallActive() || callingStatusRef.current === "Connected" || (callVisibleRef.current && callingStatusRef.current === "Connected")) {
            console.log("🛡️ [Web CRM] Suppressed duplicate ringing popup for callId:", incomingCallId);
            return;
          }
        }

        // 5. Check if incoming call service is enabled
        const isIncomingEnabled = Boolean(
          incomingCallServiceRef.current &&
          incomingCallServiceRef.current !== "0" &&
          incomingCallServiceRef.current !== 0
        );

        if (isIncomingCall && !isIncomingEnabled) {
          return;
        }

        // If outgoing call was initiated on another device (e.g. Mobile App), ignore on Web
        if (!isIncomingCall && !isLocalCallActive()) {
          console.log("🛡️ [Web CRM] Ignoring BUSINESS_INITIATED call webhook not initiated by this browser session");
          return;
        }

        if (["connect", "accept", "accepted", "ringing", "pre_accept", "offer"].includes(eventType) || rawCall.session || rawCall.sdp_answer || rawCall.sdp) {
          if (isDismissedOrHandledElsewhere && !isLocallyAcceptedCall && !isLocalCallActive()) {
            return;
          }

          const isConnected = eventType === "accept" || eventType === "accepted";
          if (isConnected) {
            updateCallingStatus("Connected");
          } else if (eventType === "connect" || eventType === "ringing") {
            updateCallingStatus(prev => (prev === "Connected" ? "Connected" : (isIncomingCall ? "Voice Calling..." : "Ringing...")));
          } else {
            updateCallingStatus(prev => (prev === "Connected" ? "Connected" : (isIncomingCall ? "Voice Calling..." : "Ringing...")));
          }

          caller_detils.current = { ...(caller_detils.current || {}), ...rawCall };
          const contactList = Array.isArray(customersRef.current?.data) ? customersRef.current.data : [];
          const caller = contactList.find(c => c?.wa_id === (rawCall.from || rawCall.wa_id || data.wa_id));
          if (caller) caller_exits.current = caller;

          const sdpAnswer = rawCall.session?.sdp || rawCall.sdp_answer || rawCall.sdp || data.sdp_answer || data.sdp;
          if (sdpAnswer && !isIncomingCall && isLocalCallActive()) {
            acceptWhatsAppAnswer(sdpAnswer);
          }

          updateCallVisible(true);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [messageListUpdate, messageDeliverStatusUpdate, reapplyTabFilter, updateCallVisible, updateCallingStatus]);

  // ── Call Initiation ────────────────────────────────────────────────────────
  const handleStartBusinessCall = useCallback(async (contact) => {
    if (!contact?.wa_id) return;
    try {
      updateCallingStatus("Calling...");
      caller_exits.current = contact;
      caller_detils.current = {
        id: `call_${Date.now()}`,
        direction: "BUSINESS_INITIATED",
        from: contact.wa_id,
        name: contact.name || contact.wa_id,
        wa_id: contact.wa_id,
      };
      updateCallVisible(true);

      const sdpOffer = await createSDPoffer();
      if (!sdpOffer) {
        ErrorToastContainer("Failed to generate call offer");
        updateCallVisible(false);
        endCallSession();
        return;
      }

      const token = getToken();
      const formdata = new FormData();
      formdata.append("mobile_no", contact.wa_id);
      formdata.append("sdp_offer", sdpOffer);

      const endpoint = `${domain()}/call_stablish`;

      if (isCallRecordingEnabled) {
        formdata.append("recording_status", "ENABLED");
        formdata.append("status", "ENABLED");
        formdata.append("purpose", "quality assurance");
        formdata.append("recording_purpose", "quality assurance");
        formdata.append("announcement_language", "en_US");
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: new Headers({ Authorization: `Bearer ${token}` }),
        body: formdata,
        redirect: "follow",
      });

      let result;
      try {
        result = await response.json();
      } catch (e) {
        result = { status: false, message: `Server error (${response.status})` };
      }

      if (result.status === true) {
        if (result.call_id) {
          caller_detils.current.id = result.call_id;
          try { sessionStorage.setItem('active_accepted_call_id', result.call_id); } catch (_) {}
        }
        updateCallingStatus("Ringing...");
      } else {
        ErrorToastContainer(result.message || "Call Establish Failed!");
        updateCallVisible(false);
        endCallSession();
      }
    } catch (err) {
      console.error("Error initiating business call:", err);
      ErrorToastContainer("Call Establish Error!");
      updateCallVisible(false);
      endCallSession();
    }
  }, [isCallRecordingEnabled, updateCallVisible, updateCallingStatus]);

  // ── Tab switch handler ───────────────────────────────────────────────────────

  const handleTabClick = useCallback((tabId) => {
    if (activeTabRef.current === tabId) return;
    activeTabRef.current = tabId;
    setActiveTab(tabId);
    switch (tabId) {
      case "active":    active(); break;
      case "ai":        ai(); break;
      case "intervent": intervent(); break;
      default: break;
    }
  }, [active, ai, intervent]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return <Preloader action={loading} />;

  const safeCustomers = Array.isArray(customers) ? customers : [];
  const safeTotalCustomers = Array.isArray(customersRef.current?.data) ? customersRef.current.data.length : 0;

  return (
    <ChatContext.Provider
      value={{ wadetails, setWadetails, customers: safeCustomers, customersRef, active, ai, intervent, customerschat, setCustomerschat }}
    >
      <div 
        className="w-100 p-0 m-0 card rounded-0 border-0" 
        style={{ height: "calc(100vh - 70px)", overflow: "hidden", backgroundColor: "#fff" }}
      >
        <Call
          caller_detils={caller_detils}
          caller_exits={caller_exits}
          visible={callVisible}
          setVisible={updateCallVisible}
          calling_status={calling_status}
          setCallingStatus={updateCallingStatus}
        />

        <div className="d-flex flex-row h-100 position-relative w-100" style={{ overflow: "hidden" }}>
          
          {/* ── Contact List Sidebar ────────────────────────────────────────────── */}
          <div 
            className={`whatsapp-contact-list-pane ${wadetails ? "d-none d-lg-flex" : "d-flex"}`}
            style={{ 
              width: "360px",
              minWidth: "320px",
              maxWidth: "380px",
              flexShrink: 0,
              height: "100%", 
              flexDirection: "column",
              backgroundColor: "#F0F2F5",
              borderRight: "1px solid #e0e0e0"
            }}
          >
            {/* Header section */}
            <div className="d-flex flex-column p-2 p-md-3 border-bottom" style={{ backgroundColor: "#F0F2F5" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold" style={{ color: "#111B21" }}>
                  Contacts{" "}
                  <small className="text-muted ms-1">
                    ({safeCustomers.length}/{safeTotalCustomers})
                  </small>
                </h6>
                <Startnewchat
                  tabFunction={reapplyTabFilter}
                  customers={customersRef}
                />
              </div>

              {/* Tab buttons */}
              <div className="d-flex justify-content-between mb-2 gap-1" role="tablist" aria-label="Chat filters">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className="btn btn-sm px-2 px-md-3 py-2 rounded-pill fw-semibold flex-shrink-0"
                    style={{
                      backgroundColor: activeTab === tab.id ? "rgb(39, 92, 83)" : "#E4E6EB",
                      color: activeTab === tab.id ? "white" : "rgb(39, 92, 83)",
                      border: "none",
                      fontSize: "0.875rem",
                      minWidth: "fit-content",
                      transition: "background-color 0.2s ease, color 0.2s ease",
                    }}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <input
                type="text"
                className="form-control form-control-sm rounded-pill"
                placeholder="Search or start new chat"
                ref={searchRef}
                onChange={handleSearchChange}
                aria-label="Search contacts"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: "#111B21",
                  border: "1px solid #D1D1D1",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
            </div>

            {/* Contact list scrollable area */}
            <div
              className="flex-grow-1 overflow-auto px-1 px-md-2 py-1"
              style={{ backgroundColor: "#F0F2F5" }}
              onScroll={handleScroll}
              role="list"
              aria-label="Contacts"
            >
              {safeCustomers.length > 0 ? (
                <>
                  {safeCustomers.map(customer => (
                    <span
                      key={customer.wa_id}
                      role="listitem"
                      onClick={() => {
                        if (wadetails?.wa_id !== customer.wa_id) {
                          setCustomerschat(null);
                          setWadetails(customer);
                          wadetailsRef.current = customer;
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === "Enter" && wadetails?.wa_id !== customer.wa_id) {
                          setCustomerschat(null);
                          setWadetails(customer);
                          wadetailsRef.current = customer;
                        }
                      }}
                    >
                      <Customer
                        customer={customer}
                        isActive={wadetails?.wa_id === customer.wa_id}
                      />
                    </span>
                  ))}
                  <LoadingIndicator.Component />
                </>
              ) : (
                <p className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                  No contacts found for this filter/search.
                </p>
              )}
            </div>
          </div>

          {/* ── Main Chat Area ─────────────────────────────────────────────────── */}
          <div 
            className={`flex-grow-1 h-100 overflow-hidden ${wadetails ? "d-flex" : "d-none d-lg-flex"}`}
            style={{ 
              flexDirection: "column",
              minWidth: 0,
              backgroundColor: "#E5DDD5"
            }}
          >
            {wadetails ? (
              <Customerchat
                wadetails={wadetails}
                customerschat={customerschat}
                setCustomerschat={setCustomerschat}
                onBackClick={() => {
                  setWadetails(null);
                  wadetailsRef.current = null;
                }}
                onStartCall={handleStartBusinessCall}
              />
            ) : (
              <Defaultchat />
            )}
          </div>

        </div>

        <WebSocketNotification />
        <WebSocketStatus />

        <style>{`
          @media (max-width: 991px) {
            .whatsapp-contact-list-pane {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
            }
          }
        `}</style>
      </div>
    </ChatContext.Provider>
  );
}